import { useState, useEffect } from 'react';
import { associateCallReport } from '../../services/callService';
import { searchCallReports } from '../../services/callReportService';
import type { RetellCallReport } from '../../types/callReport';

interface AssociateCallReportModalProps {
  callId: string;
  retellAgentId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AssociateCallReportModal({ callId, retellAgentId, onClose, onSuccess }: AssociateCallReportModalProps) {
  const [reports, setReports] = useState<RetellCallReport[]>([]);
  const [selectedReportId, setSelectedReportId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const filter = retellAgentId ? { agentId: retellAgentId } : {};
    searchCallReports(filter, { pageSize: 1000, orderBy: 'name', order: 'asc' })
      .then((results) => {
        setReports(results);
        if (results.length === 1) setSelectedReportId(results[0].id);
      })
      .catch(() => setError('Failed to load call reports'));
  }, [retellAgentId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReportId) return;

    setError('');
    setLoading(true);
    try {
      await associateCallReport(callId, selectedReportId);
      onSuccess();
    } catch {
      setError('Failed to associate call report');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Associate Call Report</h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Call Report *</label>
                <select
                  value={selectedReportId}
                  onChange={(e) => setSelectedReportId(e.target.value)}
                  required
                  className={inputClass}
                >
                  <option value="">Select a call report</option>
                  {reports.map((r) => (
                    <option key={r.id} value={r.id}>{r.name || r.id}</option>
                  ))}
                </select>
              </div>
            </div>
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedReportId}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Associating...' : 'Associate Call Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
